import { z } from "zod";

const utf8ByteLength = (value: string): number => new TextEncoder().encode(value).byteLength;
const withinUtf8Bytes =
  (maximum: number) =>
  (value: string): boolean =>
    utf8ByteLength(value) <= maximum;
const trimmedText = (maximum: number, message = "Enter a value") =>
  z
    .string()
    .trim()
    .min(1, message)
    .max(maximum)
    .refine(withinUtf8Bytes(maximum), `Use no more than ${maximum} bytes`);
const nullableFormText = (maximum: number) =>
  z
    .union([z.string(), z.null()])
    .optional()
    .transform((value) => {
      const trimmed = value?.trim() ?? "";
      return trimmed.length === 0 ? null : trimmed;
    })
    .refine((value) => value === null || utf8ByteLength(value) <= maximum);
const validTimezone = (value: string): boolean => {
  try {
    new Intl.DateTimeFormat("en", { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
};

export const propertyIdSchema = z.string().regex(/^[0-9a-f]{32}$/u);
export const propertyTypeSchema = z.enum(["HOUSE", "APARTMENT", "CONDO", "TOWNHOUSE", "OTHER"]);
export const propertyStatusSchema = z.enum(["ACTIVE", "INACTIVE", "ARCHIVED"]);
export const propertyBedSizeSchema = z.enum([
  "TWIN",
  "TWIN_XL",
  "FULL",
  "QUEEN",
  "KING",
  "CAL_KING",
  "SOFA",
  "OTHER",
]);
const versionNoSchema = z.number().int().min(1);
const isoDateTimeSchema = z.string().datetime({ offset: true });
const bedroomCountSchema = z.number().int().min(0).max(999);
const bathroomCountSchema = z.number().min(0).max(999.9).multipleOf(0.5);
const roomCountSchema = z.number().int().min(0).max(999);
const sizeSqftSchema = z.number().int().min(1).max(99_999_999);

export const propertyAddressInputSchema = z.strictObject({
  line1: trimmedText(200, "Enter the first address line"),
  line2: nullableFormText(200),
  locality: trimmedText(100, "Enter the town or city"),
  regionCode: nullableFormText(100),
  postalCode: trimmedText(30, "Enter the postal code"),
  countryCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z]{2}$/u, "Use a two-letter country code"),
  timezoneRegion: trimmedText(64, "Enter the timezone").refine(
    validTimezone,
    "Enter a valid timezone",
  ),
});

export const propertyAddressSchema = z.strictObject({
  id: propertyIdSchema,
  label: z.string().min(1),
  versionNo: versionNoSchema,
  line1: z.string().min(1),
  line2: z.string().nullable(),
  locality: z.string().min(1),
  regionCode: z.string().nullable(),
  postalCode: z.string().min(1),
  countryCode: z.string().regex(/^[A-Z]{2}$/u),
  timezoneRegion: z.string().min(1),
  validFrom: isoDateTimeSchema,
});

export const propertyBedInputSchema = z.strictObject({
  label: trimmedText(80, "Enter a bed label"),
  roomLabel: nullableFormText(80),
  size: propertyBedSizeSchema,
  mattressWidthCm: z
    .number()
    .positive()
    .max(9_999.99)
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  mattressLengthCm: z
    .number()
    .positive()
    .max(9_999.99)
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  quantity: z.number().int().min(1).max(99),
});

export const propertyBedSchema = z.strictObject({
  id: propertyIdSchema,
  label: z.string().min(1),
  roomLabel: z.string().nullable(),
  size: propertyBedSizeSchema,
  mattressWidthCm: z.number().positive().nullable(),
  mattressLengthCm: z.number().positive().nullable(),
  quantity: z.number().int().min(1).max(99),
  versionNo: versionNoSchema,
});

export const propertySummarySchema = z.strictObject({
  id: propertyIdSchema,
  propertyCode: z.string().min(1),
  propertyName: z.string().min(1),
  propertyType: propertyTypeSchema,
  bedroomCount: bedroomCountSchema,
  bathroomCount: bathroomCountSchema,
  roomCount: roomCountSchema.nullable(),
  sizeSqft: sizeSqftSchema.nullable(),
  status: propertyStatusSchema,
  versionNo: versionNoSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  serviceAddress: propertyAddressSchema,
});

export const propertyDetailSchema = propertySummarySchema.extend({
  accessNotes: z.string().nullable(),
  beds: z.array(propertyBedSchema).max(100),
});

export const createPropertyAddressInputSchema = z.strictObject({
  line1: trimmedText(200, "Enter the first address line"),
  line2: nullableFormText(200),
  cityCode: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{0,63}$/u),
  postalCode: z
    .string()
    .trim()
    .regex(/^\d{5}$/u, "Choose a ZIP code"),
});

export const createPropertyInputSchema = z.strictObject({
  propertyName: trimmedText(200, "Enter a property name"),
  propertyType: propertyTypeSchema,
  bedroomCount: bedroomCountSchema,
  bathroomCount: bathroomCountSchema,
  roomCount: roomCountSchema
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  sizeSqft: sizeSqftSchema
    .nullable()
    .optional()
    .transform((value) => value ?? null),
  accessNotes: nullableFormText(4_000),
  serviceAddress: createPropertyAddressInputSchema,
});

export const updatePropertyInputSchema = z
  .strictObject({
    versionNo: versionNoSchema,
    propertyName: trimmedText(200, "Enter a property name").optional(),
    propertyType: propertyTypeSchema.optional(),
    bedroomCount: bedroomCountSchema.optional(),
    bathroomCount: bathroomCountSchema.optional(),
    roomCount: roomCountSchema.nullable().optional(),
    sizeSqft: sizeSqftSchema.nullable().optional(),
    accessNotes: z.string().trim().max(4_000).nullable().optional(),
  })
  .refine(
    (value) =>
      Object.entries(value).some(
        ([key, fieldValue]) => key !== "versionNo" && fieldValue !== undefined,
      ),
    "Change at least one field",
  );

export const replacePropertyAddressInputSchema = z.strictObject({
  versionNo: versionNoSchema,
  serviceAddress: propertyAddressInputSchema,
});

export const replacePropertyBedsInputSchema = z
  .strictObject({
    versionNo: versionNoSchema,
    beds: z.array(propertyBedInputSchema).max(100),
  })
  .superRefine(({ beds }, context) => {
    const labels = new Set<string>();
    beds.forEach((bed, index) => {
      const normalized = bed.label.toLocaleLowerCase("en-US");
      if (labels.has(normalized)) {
        context.addIssue({
          code: "custom",
          path: ["beds", index, "label"],
          message: "Use a unique label for each bed",
        });
      }
      labels.add(normalized);
    });
  });

export const propertyListResponseSchema = z.strictObject({
  data: z.array(propertySummarySchema),
});
export const propertyResponseSchema = z.strictObject({
  status: z.literal("ok"),
  property: propertyDetailSchema,
});
export const createPropertyResponseSchema = z.strictObject({
  status: z.enum(["created", "existing"]),
  property: propertyDetailSchema,
});
export const archivePropertyResponseSchema = z.strictObject({
  status: z.enum(["archived", "existing"]),
  property: propertyDetailSchema,
});
export const propertyAddressListResponseSchema = z.strictObject({
  data: z.array(propertyAddressSchema),
});
export const propertyBedListResponseSchema = z.strictObject({
  data: z.array(propertyBedSchema).max(100),
});

export type PropertyType = z.infer<typeof propertyTypeSchema>;
export type PropertyBedSize = z.infer<typeof propertyBedSizeSchema>;
export type PropertyAddressInput = z.infer<typeof propertyAddressInputSchema>;
export type CreatePropertyAddressInput = z.infer<typeof createPropertyAddressInputSchema>;
export type PropertyAddress = z.infer<typeof propertyAddressSchema>;
export type PropertyBedInput = z.infer<typeof propertyBedInputSchema>;
export type PropertyBed = z.infer<typeof propertyBedSchema>;
export type PropertySummary = z.infer<typeof propertySummarySchema>;
export type PropertyDetail = z.infer<typeof propertyDetailSchema>;
export type CreatePropertyInput = z.infer<typeof createPropertyInputSchema>;
export type UpdatePropertyInput = z.infer<typeof updatePropertyInputSchema>;
export type ReplacePropertyAddressInput = z.infer<typeof replacePropertyAddressInputSchema>;
export type ReplacePropertyBedsInput = z.infer<typeof replacePropertyBedsInputSchema>;
